import axios from '../config/axios';
import { getApiUrl } from '../config/environment';
import { RECEIPT_PAYMENT_LABELS } from '../constants/receivablePaymentMethods';
import { buildLetterheadTopSpacerHtml, clinicPrintAccentVarsStyle } from './clinicPrintLetterhead';
import { printClinicHtml } from './clinicPrintHtml';
import { formatTaxIdDisplay, taxIdColonLabel, taxIdLabel } from './taxIdRegional';
import { postalCodeInlineSuffix } from './addressRegional';
import { formatMarketCurrency } from './moneyRegional';

const paymentLabels = RECEIPT_PAYMENT_LABELS;

const formatCurrency = (v: number) => formatMarketCurrency(v || 0);

function escapeHtml(s: string | undefined | null): string {
  if (s == null || s === '') return '';
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function buildReceiptClinicBlockHtml(c: any): string {
  if (!c) {
    return `
      <div class="doc-head-center">
        <div class="clinic-name">Clínica</div>
        <div class="clinic-subtitle">Configure nome, endereço e logo em Clínica › Configurações</div>
      </div>
      <div class="doc-head-meta"></div>`;
  }
  const logo = c.logo_url
    ? `<div class="doc-head-logo"><img src="${escapeHtml(c.logo_url)}" alt="" /></div>`
    : '<div class="doc-head-logo doc-head-logo--empty"></div>';
  const subtitle = c.clinic_subtitle
    ? `<div class="clinic-subtitle">${escapeHtml(c.clinic_subtitle)}</div>`
    : '';
  const cnpjLine = c.cnpj ? `<div>CNPJ: ${escapeHtml(c.cnpj)}</div>` : '';
  let addr = '';
  if (c.address_street) {
    addr = `<div>${escapeHtml(c.address_street)}${c.address_number ? `, ${escapeHtml(c.address_number)}` : ''}${c.address_neighborhood ? ` — ${escapeHtml(c.address_neighborhood)}` : ''}</div>`;
  }
  const cityLine =
    c.address_city || c.address_state || c.address_zip
      ? `<div>${[c.address_city, c.address_state].filter(Boolean).map(escapeHtml).join(' - ')}${c.address_zip ? escapeHtml(postalCodeInlineSuffix(c.address_zip)) : ''}</div>`
      : '';
  const tel = c.phone || c.whatsapp ? `<div>Tel: ${escapeHtml(c.phone || c.whatsapp)}</div>` : '';
  const mail = c.email ? `<div>${escapeHtml(c.email)}</div>` : '';
  const site = c.website ? `<div>${escapeHtml(c.website)}</div>` : '';

  return `
    ${logo}
    <div class="doc-head-center">
      <div class="clinic-name">${escapeHtml(c.clinic_name || 'Clínica')}</div>
      ${subtitle}
    </div>
    <div class="doc-head-meta">
      ${cnpjLine}
      ${addr}
      ${cityLine}
      ${tel}
      ${mail}
      ${site}
    </div>`;
}

export function buildClinicReceiptPrintHtml(receipt: any, clinic: any): string {
  const r = receipt;
  const c = clinic;
  const issueStr = r?.issue_date ? new Date(r.issue_date + 'T12:00:00').toLocaleDateString('pt-BR') : '';
  const patientName = r?.patient_snapshot?.full_name || 'N/A';
  const patientCpf = r?.patient_snapshot?.cpf;
  const profName = r?.professional_snapshot?.full_name || '';
  const profReg =
    [r?.professional_snapshot?.registration_type, r?.professional_snapshot?.registration_number].filter(Boolean).join(' ')
    || '';
  const cancelled = r?.status === 'cancelled';

  const profSigUrl = (r?.professional_snapshot?.signature_image_url || '').trim();
  const profSigHtml = profSigUrl
    ? `<div class="prof-sig-print"><img src="${escapeHtml(profSigUrl)}" alt="" /></div>`
    : '';

  const servicesBlock = r?.services_description
    ? `<div class="section"><div class="section-title">Detalhamento dos serviços</div><div class="field multiline">${escapeHtml(r.services_description).replace(/\n/g, '<br/>')}</div></div>`
    : '';

  const notesBlock = r?.notes
    ? `<div class="section"><div class="section-title">Observações</div><div class="field multiline">${escapeHtml(r.notes).replace(/\n/g, '<br/>')}</div></div>`
    : '';

  return `<!DOCTYPE html><html lang="pt-BR"><head><meta charset="utf-8"><title></title>
<style>
  :root { --lh-accent: #2563eb; --lh-accent-rgb: 37, 99, 235; }
  * { box-sizing: border-box; }
  body { font-family: 'Segoe UI', system-ui, -apple-system, sans-serif; color: #1e293b; margin: 0; padding: 24px; background: #eef1f6; -webkit-font-smoothing: antialiased; }
  .sheet { max-width: 210mm; margin: 0 auto; background: #fff; padding: 34px 40px 40px; border-radius: 10px; box-shadow: 0 2px 16px rgba(15,23,42,0.10); }
  .doc-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 24px; border-bottom: 1px solid rgba(var(--lh-accent-rgb), 0.30); padding-bottom: 18px; margin-bottom: 26px; position: relative; }
  .doc-head::after { content: ''; position: absolute; left: 0; bottom: -2px; width: 96px; height: 3px; background: var(--lh-accent); border-radius: 2px; }
  .doc-head-logo { flex-shrink: 0; min-width: 120px; }
  .doc-head-logo img { display: block; max-width: 170px; max-height: 70px; width: auto; height: auto; object-fit: contain; object-position: left top; }
  .doc-head-logo--empty { min-height: 48px; }
  .doc-head-center { flex: 1; min-width: 0; text-align: center; align-self: center; }
  .doc-head-meta { flex-shrink: 0; max-width: 44%; font-size: 9.5px; color: #5b6b80; text-align: right; line-height: 1.75; letter-spacing: 0.015em; padding-left: 18px; border-left: 1px solid #e8edf3; align-self: stretch; }
  .clinic-name { font-family: Georgia, 'Times New Roman', serif; font-size: 21px; font-weight: 700; color: var(--lh-accent); margin: 0; letter-spacing: 0.015em; }
  .clinic-subtitle { font-size: 9.5px; color: #64748b; margin-top: 5px; text-transform: uppercase; letter-spacing: 0.18em; font-weight: 600; }
  .doc-badge { text-align: center; margin-bottom: 10px; }
  .doc-badge span { display: inline-block; padding: 6px 24px; border-radius: 999px; font-size: 10px; font-weight: 700; letter-spacing: 0.18em; text-transform: uppercase; background: rgba(var(--lh-accent-rgb), 0.05); color: var(--lh-accent); border: 1px solid rgba(var(--lh-accent-rgb), 0.35); }
  .doc-badge.cancelled span { background: #fef2f2; color: #b91c1c; border-color: #fecaca; }
  .doc-title { text-align: center; font-size: 16px; font-weight: 700; color: #0f172a; margin: 0 0 4px; text-transform: uppercase; letter-spacing: 0.14em; }
  .doc-number { text-align: center; font-size: 11px; color: #64748b; margin-bottom: 20px; letter-spacing: 0.03em; }
  .amount-wrap { text-align: center; margin: 20px 0 12px; padding: 20px 16px; background: rgba(var(--lh-accent-rgb), 0.04); border: 1px solid rgba(var(--lh-accent-rgb), 0.20); border-radius: 8px; }
  .amount { font-size: 30px; font-weight: 800; color: var(--lh-accent); letter-spacing: -0.02em; }
  .amount-words { font-size: 12.5px; color: #475569; font-style: italic; margin-top: 8px; line-height: 1.5; }
  .section { margin-bottom: 18px; }
  .section-title { font-size: 10px; font-weight: 700; color: var(--lh-accent); text-transform: uppercase; letter-spacing: 0.14em; margin-bottom: 9px; padding-bottom: 5px; border-bottom: 1px solid #e8edf3; }
  .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 6px 20px; }
  .field { font-size: 13px; line-height: 1.5; color: #334155; }
  .field strong { color: #0f172a; font-weight: 600; }
  .field.multiline { white-space: normal; }
  .signatures { display: flex; justify-content: space-between; gap: 24px; margin-top: 48px; padding-top: 8px; }
  .signature-line { flex: 1; text-align: center; }
  .signature-line hr { border: none; border-top: 1px solid #334155; margin: 0 0 8px; }
  .signature-line .name { font-size: 13px; font-weight: 600; color: #0f172a; }
  .signature-line .reg { font-size: 11px; color: #64748b; margin-top: 2px; }
  .prof-sig-print { text-align: center; margin-bottom: 6px; min-height: 0; }
  .prof-sig-print img { max-height: 90px; max-width: 360px; object-fit: contain; display: inline-block; vertical-align: bottom; }
  .footer-note { margin-top: 30px; padding-top: 14px; border-top: 1px solid #eef1f6; font-size: 9px; color: #9aa7b8; text-align: center; line-height: 1.6; letter-spacing: 0.06em; }
  @media print {
    body { background: #fff; padding: 0; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    .sheet { box-shadow: none; border-radius: 0; max-width: none; }
    @page { size: A4; margin: 14mm; }
  }
</style></head><body style="${clinicPrintAccentVarsStyle(c)}">
<div class="sheet">
  ${c?.letterhead_print_enabled !== false ? `<div class="doc-head">${buildReceiptClinicBlockHtml(c)}</div>` : buildLetterheadTopSpacerHtml(c)}
  <div class="doc-badge${cancelled ? ' cancelled' : ''}"><span>${cancelled ? 'Recibo cancelado' : 'Recibo de pagamento'}</span></div>
  <h1 class="doc-title">${cancelled ? 'RECIBO (cancelado)' : 'RECIBO DE PAGAMENTO'}</h1>
  <div class="doc-number">Nº ${escapeHtml(r?.receipt_number || '—')} ${issueStr ? `• Emissão: ${escapeHtml(issueStr)}` : ''}</div>
  <div class="amount-wrap">
    <div class="amount">${formatCurrency(r?.amount || 0)}</div>
    ${r?.amount_in_words ? `<div class="amount-words">(${escapeHtml(r.amount_in_words)})</div>` : ''}
  </div>
  <div class="section">
    <div class="section-title">Pagador</div>
    <div class="info-grid">
      <div class="field"><strong>Recebi de:</strong> ${escapeHtml(patientName)}</div>
      ${patientCpf ? `<div class="field"><strong>${taxIdColonLabel()}</strong> ${escapeHtml(formatTaxIdDisplay(patientCpf))}</div>` : '<div></div>'}
    </div>
  </div>
  <div class="section">
    <div class="section-title">Referência e pagamento</div>
    <div class="field"><strong>Referente a:</strong> ${escapeHtml(r?.description || '—')}</div>
    <div class="field" style="margin-top:8px"><strong>Forma de pagamento:</strong> ${escapeHtml(paymentLabels[r?.payment_method] || r?.payment_method || '—')}${r?.installment_info ? escapeHtml(` — ${r.installment_info}`) : ''}</div>
  </div>
  ${servicesBlock}
  ${notesBlock}
  <div class="signatures">
    <div class="signature-line">
      ${profSigHtml}
      <hr />
      <div class="name">${escapeHtml(profName || c?.clinic_name || 'Profissional')}</div>
      ${profReg ? `<div class="reg">${escapeHtml(profReg)}</div>` : ''}
    </div>
    <div class="signature-line">
      <hr />
      <div class="name">${escapeHtml(patientName)}</div>
      ${patientCpf ? `<div class="reg">${taxIdLabel()} ${escapeHtml(formatTaxIdDisplay(patientCpf))}</div>` : '<div class="reg">Pagador</div>'}
    </div>
  </div>
  <div class="footer-note">Documento emitido eletronicamente. Em caso de dúvidas, entre em contato com a clínica pelos dados no cabeçalho.</div>
</div>
</body></html>`;
}

export function printClinicReceiptData(printData: { receipt: any; clinic: any }) {
  const html = buildClinicReceiptPrintHtml(printData.receipt, printData.clinic);
  const title = `Recibo ${printData.receipt?.receipt_number || ''}`.trim();
  printClinicHtml(html, { title });
}

export async function printClinicReceiptById(receiptId: string) {
  const { data } = await axios.get(getApiUrl(`/clinic/receipts/${receiptId}/print`));
  printClinicReceiptData(data.data);
}

export async function printClinicReceiptFromTransaction(transactionId: string) {
  const { data } = await axios.post(getApiUrl(`/clinic/receipts/from-transaction/${transactionId}`));
  printClinicReceiptData(data.data);
}
